/* site-runtime.js — the ONE observability loader of the Interview Clozo site (SITE-DESIGN §2.8, OBSERVABILITY §2, §5.2,
   §6.5; CONTRACT R11, R31, §6, §7). Loaded `defer` from components/navbar.html, LAST in document order, on every page
   that bakes the navbar = every user-facing page. Never on /admin/* (that page does not bake the navbar and this file
   returns there as a second guard, D12.19).

   window.__clozoRuntime = { config, track, reportFailure, clarityEvent, setUser }

   1. config()  — R31 /api/site-config, cached 10 min in sessionStorage; any failure = the inert default.
   2. Clarity   — every user-facing page but a strict-CSP one (billing v2 WP6, A6-12: account, success, cancel, the
                  affiliate portal carry data-clozo-csp="strict" and load no third-party script), EVERY visitor, no
                  consent wait (D12.10, D13.3); consentv2 "granted" right
                  after the tag (D15.3); NEVER identify, never a custom id (D15.21); default masking (no mask attributes).
                  Id = the baked data-clozo-clarity attribute (navbar.html), R31 clarity.projectId only when nothing is
                  baked. No id anywhere = inert (the owner has not supplied one yet).
   3. Sentry    — inert while R31 sentry.dsn is null (D12.15: only the owner's Interview project DSN, never Clozo's).
                  With a DSN: the vendored 11.0.0 tracing bundle, tracing on every page (billing + sign-in pages at the
                  R31 billing rate), scrubbers on every hook, user = { id: <account uuid> } only, no replay.
   4. Events    — first-party R11 /api/events, ALWAYS ON for every signed-in visitor (D13.3): no consent read, no GPC
                  read. Signed-out visitors send nothing (R11 is A-USER). Content-free (CONTRACT §6.5).
   Observability must never break a page: every entry point is wrapped in try/catch. ES5, no inline handlers. */
(function () {
  'use strict';
  if (/^\/admin(\/|$)/.test(location.pathname)) return;                                   // D12.19
  var R = window.__clozoRuntime = window.__clozoRuntime || {};
  if (R.__loaded) return;
  R.__loaded = true;

  var SELF = document.currentScript || document.querySelector('script[data-clozo-clarity]');
  var BAKED = '';
  try { BAKED = String((SELF && SELF.getAttribute('data-clozo-clarity')) || ''); } catch (_) {}
  var ID_RE = /^[a-z0-9]{6,16}$/;
  var CFG_KEY = 'clozo_site_config_v1', CFG_TTL = 10 * 60 * 1000;
  var INERT = { sentry: { dsn: null }, clarity: { projectId: null } };
  // CONTRACT §0.9. subscription_id / ba_token belonged to the retired second provider's return links: still scrubbed for one
  // release (old links sit in mailboxes; billing v2 WP10 slice C, D-16 / M5 K9).
  var CAP_NAMES = ['t', 'c', 'token', 'session_id', 'subscription_id', 'ba_token'];

  function now() { return Date.now(); }
  function idle(fn) {
    var run = function () { try { fn(); } catch (_) {} };
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 3000 });
    else setTimeout(run, 2000);                                                           // Safari
  }
  function afterLoad(fn) {
    if (document.readyState === 'complete') idle(fn);
    else window.addEventListener('load', function () { idle(fn); });
  }

  /* ---------------- page enum (CONTRACT §6.2 `page`) ---------------- */
  var LEGAL = ['terms', 'privacy', 'refund-policy', 'cookie-policy', 'shipping', 'acceptable-use', 'disclaimer'];
  var PAGES = { '/': 'home', '/index': 'home', '/pricing': 'pricing', '/features': 'features', '/account': 'account',
    '/success': 'success', '/install': 'install', '/cancel': 'cancel', '/login': 'login', '/reset': 'reset',
    '/verify': 'verify', '/manage': 'manage', '/welcome': 'welcome', '/app-signin': 'app_signin', '/faq': 'faq',
    '/salary-negotiation-book': 'book', '/book-link': 'book_link', '/app-link': 'app_link' };
  function pageOf(pathname) {
    var p = String(pathname || '/').replace(/\.html$/, '').replace(/\/+$/, '') || '/';
    if (PAGES.hasOwnProperty(p)) return PAGES[p];
    if (/^\/blog(\/|$)/.test(p)) return 'blog';
    if (/^\/use-cases(\/|$)/.test(p)) return 'use_case';
    if (LEGAL.indexOf(p.slice(1)) !== -1) return 'legal';
    return 'other';
  }
  var PAGE = pageOf(location.pathname);
  var BILLING_PAGES = /^\/(pricing|account|login|signup|reset|verify|manage|cancel|success|welcome|app-signin)$/;

  /* ---------------- 1. config() — R31 ---------------- */
  var cfgState = null, cfgWaiters = [], cfgStarted = false;
  function readCache() {
    try {
      var raw = sessionStorage.getItem(CFG_KEY);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (o && o.cfg && typeof o.at === 'number' && now() - o.at < CFG_TTL) return o.cfg;
    } catch (_) {}
    return null;
  }
  function settle(cfg) {
    if (cfgState) return;
    cfgState = (cfg && typeof cfg === 'object') ? cfg : INERT;
    var w = cfgWaiters; cfgWaiters = [];
    for (var i = 0; i < w.length; i++) { try { w[i](cfgState); } catch (_) {} }
  }
  function startConfig() {
    if (cfgStarted) return;
    cfgStarted = true;
    var cached = readCache();
    if (cached) { settle(cached); return; }
    var done = false, ctrl = null;
    try { ctrl = typeof AbortController === 'function' ? new AbortController() : null; } catch (_) {}
    var timer = setTimeout(function () { if (done) return; done = true; try { ctrl && ctrl.abort(); } catch (_) {} settle(INERT); }, 3000);
    try {
      fetch('/api/site-config', { credentials: 'omit', signal: ctrl ? ctrl.signal : undefined })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (cfg) {
          if (done) return; done = true; clearTimeout(timer);
          if (cfg && typeof cfg === 'object') {
            try { sessionStorage.setItem(CFG_KEY, JSON.stringify({ at: now(), cfg: cfg })); } catch (_) {}
            settle(cfg);
          } else settle(INERT);
        }, function () { if (done) return; done = true; clearTimeout(timer); settle(INERT); });
    } catch (_) { done = true; clearTimeout(timer); settle(INERT); }
  }
  /** config(cb): cb(cfg) once R31 resolved (or failed → inert). Without cb: starts the call, returns the cfg or null. */
  R.config = function (cb) {
    if (typeof cb === 'function') { if (cfgState) { try { cb(cfgState); } catch (_) {} } else cfgWaiters.push(cb); }
    startConfig();
    return cfgState;
  };

  /* ---------------- capability left in the URL (a failed head scrub; SITE §2.8 item 3, CONTRACT §0.9) ---------------- */
  function stripCapability() {
    var found = false, cap = {};
    try {
      var u = new URL(location.href);
      var h = new URLSearchParams(String(u.hash || '').replace(/^#/, ''));
      for (var i = 0; i < CAP_NAMES.length; i++) {
        var k = CAP_NAMES[i];
        if (u.searchParams.has(k)) { cap[k] = u.searchParams.get(k); u.searchParams['delete'](k); found = true; }
        if (h.has(k)) { cap[k] = h.get(k); h['delete'](k); found = true; }
      }
      if (!found) return false;
      var hs = h.toString();
      u.hash = hs ? '#' + hs : '';
      var c = window.__clozoCap = window.__clozoCap || {};
      for (var key in cap) { if (cap.hasOwnProperty(key) && c[key] === undefined) c[key] = cap[key]; }
      try { sessionStorage.setItem('__clozoCap', JSON.stringify(c)); } catch (_) {}
      history.replaceState(history.state, '', u.pathname + u.search + u.hash);
    } catch (_) {}
    return found;
  }

  /* ---------------- 3. Sentry — scrubbers (OBSERVABILITY §2.5; CONTRACT §7) ---------------- */
  var BUNDLE = '/assets/js/vendor/sentry-browser-11.0.0.tracing.min.js';                  // exact version in the name
  var DROP_KEY = /text|transcript|reason|message|body|email|token|password|prompt|answer|resume|cookie|authorization|code/i;
  function decode(s) { s = String(s == null ? '' : s); try { return decodeURIComponent(s); } catch (_) { return s; } }
  function redact(s) {
    s = String(s == null ? '' : s)
      .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, '[email]')
      .replace(/Bearer\s+[^\s"']+/gi, '[token]')
      .replace(/\b(?:clt1|cpa1|ccs1|cih1|ccx1)\.[A-Za-z0-9._~-]+/g, '[ref]')
      .replace(/\b(?:ict|cs|sub|pi|in|seti|cus|pm)_[A-Za-z0-9_]{4,}/g, '[ref]')
      .replace(/\bI-[A-Z0-9]{6,}\b/g, '[ref]')
      .replace(/\d{6,}/g, '[n]');
    return s.length > 500 ? s.slice(0, 500) : s;
  }
  function stripUrl(u) {
    try { var x = new URL(String(u), location.origin); return x.origin + x.pathname; }
    catch (_) { return String(u || '').split(/[?#]/)[0]; }
  }
  function scrubText(s) { return redact(decode(s).replace(/[?#][^\s]*/g, '')); }
  function reduceSelector(s) {
    return String(s || '').split(/\s*>\s*/).map(function (part) {
      var tag = (part.match(/^[a-zA-Z][a-zA-Z0-9-]*/) || [''])[0];
      var id = (part.replace(/\[[^\]]*\]/g, '').match(/#[A-Za-z][\w-]*/) || [''])[0];
      return (tag + id) || '*';
    }).join(' > ');
  }
  function routeTemplate(path) {
    return String(path || '').split(/[?#]/)[0].replace(/^https?:\/\/[^/]+/, '').replace(/^\/api/, '')
      .replace(/\/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, '/:id')
      .replace(/\/(?:cs|sub|in|ict|pi)_[A-Za-z0-9_-]{6,}/g, '/:ref').replace(/\/I-[A-Za-z0-9-]{6,}/g, '/:ref');
  }
  function isUuid(v) { return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(v || '')); }
  function dropKeys(o) {
    if (!o || typeof o !== 'object') return;
    for (var k in o) { if (o.hasOwnProperty(k) && DROP_KEY.test(k)) delete o[k]; }
  }
  function scrubRequestUserExtra(ev) {
    if (ev.request) {
      if (ev.request.url) ev.request.url = stripUrl(decode(ev.request.url));
      delete ev.request.query_string; delete ev.request.cookies; delete ev.request.data;
      var h = ev.request.headers, ua = null;
      if (h) { for (var k in h) { if (h.hasOwnProperty(k) && /^user-agent$/i.test(k)) ua = h[k]; } }
      ev.request.headers = ua ? { 'User-Agent': ua } : {};
    }
    if (ev.user) { if (isUuid(ev.user.id)) ev.user = { id: ev.user.id }; else delete ev.user; }
    dropKeys(ev.extra);
    dropKeys(ev.contexts);
  }
  function scrubEvent(ev) {
    try {
      scrubRequestUserExtra(ev);
      if (ev.message) ev.message = redact(decode(ev.message));
      if (ev.logentry && ev.logentry.message) ev.logentry.message = redact(decode(ev.logentry.message));
      var vals = ev.exception && ev.exception.values;
      if (vals) for (var i = 0; i < vals.length; i++) { if (vals[i].value) vals[i].value = redact(decode(vals[i].value)); }
      if (ev.breadcrumbs) {
        var out = [];
        for (var j = 0; j < ev.breadcrumbs.length; j++) { var b = scrubBreadcrumb(ev.breadcrumbs[j]); if (b) out.push(b); }
        ev.breadcrumbs = out;
      }
      if (ev.transaction) ev.transaction = scrubText(ev.transaction);
      return ev;
    } catch (_) { return null; }                                                           // unsure → drop, never leak
  }
  function scrubBreadcrumb(b) {
    try {
      if (!b) return null;
      var cat = String(b.category || '');
      if (cat === 'ui.input' || cat === 'console') return null;
      if (cat === 'ui.click') {
        var obs = (String(b.message || '').match(/data-obs=["']?([a-z0-9_.:-]{1,64})/) || [])[1];
        var tag = (String(b.message || '').match(/^[a-zA-Z][a-zA-Z0-9-]*/) || ['element'])[0];
        return { category: 'ui.click', timestamp: b.timestamp, level: b.level,
          message: obs ? tag + '[data-obs=' + obs + ']' : tag };
      }
      if (cat === 'fetch' || cat === 'xhr') {
        if (b.data) { if (b.data.url) b.data.url = stripUrl(decode(b.data.url));
          delete b.data.request_body_size; delete b.data.response_body_size; }
        return b;
      }
      if (cat === 'navigation') {
        if (b.data) { if (b.data.from) b.data.from = stripUrl(decode(b.data.from)).replace(/^https?:\/\/[^/]+/, '');
          if (b.data.to) b.data.to = stripUrl(decode(b.data.to)).replace(/^https?:\/\/[^/]+/, ''); }
        return b;
      }
      if (b.message) b.message = scrubText(b.message);
      if (b.data) { for (var k in b.data) { if (b.data.hasOwnProperty(k)) {
        if (DROP_KEY.test(k)) delete b.data[k]; else if (typeof b.data[k] === 'string') b.data[k] = scrubText(b.data[k]); } } }
      return b;
    } catch (_) { return null; }
  }
  var SELECTOR_SPAN = /^ui\.(interaction|webvital)/;
  function scrubSelector(x) { return redact(reduceSelector(decode(x))); }   // #ids are selectors here, not fragments
  function scrubSpan(s) {
    try {
      if (!s) return s;
      var op = String(s.op || '');
      if (s.description) s.description = SELECTOR_SPAN.test(op) ? scrubSelector(s.description) : scrubText(s.description);
      if (s.name) s.name = SELECTOR_SPAN.test(op) ? scrubSelector(s.name) : scrubText(s.name);
      var a = s.data || s.attributes;
      if (a) {
        delete a['http.query']; delete a['url.query']; delete a['http.fragment']; delete a['url.fragment'];
        for (var k in a) { if (a.hasOwnProperty(k) && typeof a[k] === 'string') {
          a[k] = (/lcp\.element|cls\.source/.test(k)) ? scrubSelector(a[k]) : scrubText(a[k]); } }
      }
      return s;
    } catch (_) { return s; }
  }
  function scrubTransaction(t) {
    try {
      scrubRequestUserExtra(t);
      t.transaction = stripUrl(decode(t.transaction || location.pathname)).replace(/^https?:\/\/[^/]+/, '') || '/';
      if (t.contexts && t.contexts.trace) scrubSpan(t.contexts.trace);
      if (t.spans) for (var i = 0; i < t.spans.length; i++) scrubSpan(t.spans[i]);
      if (t.breadcrumbs) { var out = []; for (var j = 0; j < t.breadcrumbs.length; j++) { var b = scrubBreadcrumb(t.breadcrumbs[j]); if (b) out.push(b); } t.breadcrumbs = out; }
      return t;
    } catch (_) { return null; }
  }
  function tracesRate(cfg, pathname) {
    var p = String(pathname || '/').replace(/\.html$/, '').replace(/\/$/, '') || '/';
    var billing = BILLING_PAGES.test(p);
    var rate = billing && typeof cfg.tracesSampleRateBilling === 'number' ? cfg.tracesSampleRateBilling : cfg.tracesSampleRate;
    return (typeof rate === 'number' && rate > 0 && rate <= 1) ? rate : 0.02;          // never 0 on a user-facing page
  }

  /* ---------------- 3. Sentry — module (OBSERVABILITY §2.2–§2.7) ---------------- */
  var early = window.__clozoEarly;
  if (!early || typeof early.push !== 'function') early = window.__clozoEarly = [];
  if (!window.__clozoEarlyHooked) {                        // the navbar bootstrap normally registers these; guard if not
    window.__clozoEarlyHooked = true;
    window.addEventListener('error', function (e) { try { if (early.length < 10) early.push({ error: e && e.error, message: e && e.message }); } catch (_) {} });
    window.addEventListener('unhandledrejection', function (e) { try { if (early.length < 10) early.push({ error: e && e.reason }); } catch (_) {} });
  }
  var sState = 'idle', sLoading = false, sSent = 0, MAX_PER_PAGE = 20, pending = [], netSeen = {};
  var CODE_RE = /^[a-z0-9_]{1,64}$/;
  var EXPECTED = [
    'invalid_credentials', 'login_required', 'session_invalid', 'email_taken', 'username_taken', 'invalid_email',
    'weak_password', 'email_in_use', 'password_required', 'invalid_token', 'signup_blocked', 'wrong_password',
    'reauth_required', 'link_used', 'link_expired', 'link_invalid', 'code_expired', 'code_used', 'code_invalid',
    'signin_unavailable', 'rate_limited',
    'reason_required', 'step_required', 'intent_required', 'cancel_session_expired', 'otp_invalid', 'otp_expired',
    'otp_locked', 'reauth_locked', 'password_not_set', 'voice_not_available', 'audio_too_large', 'audio_too_long',
    'audio_type_unsupported', 'intent_closed', 'discount_unavailable', 'no_recurring_billing', 'nothing_to_cancel',
    'already_entitled', 'already_subscribed', 'trial_already_used', 'use_in_place_action',
    'cancel_scheduled', 'not_trialing', 'amount_changed', 'disclosure_changed', 'preview_expired', 'not_eligible',
    'provider_not_supported', 'period_ended', 'portal_unavailable_provider',
    'trial_charge_declined', 'trial_charge_action_required', 'renew_declined', 'renew_action_required',
    'quote_unavailable', 'free_trial_exhausted'];

  function sentryInit(sc) {
    var S = window.Sentry;
    if (!S || typeof S.init !== 'function') { sOff(); return; }
    S.init({
      dsn: sc.dsn,
      environment: sc.environment || 'interview-web',
      release: 'interview@' + (sc.release || 'unknown'),
      sendDefaultPii: false,
      dataCollection: { userInfo: false, cookies: false, httpHeaders: false, httpBodies: [], urlQueryParams: false,
        graphQL: { document: false, variables: false }, genAI: { inputs: false, outputs: false },
        databaseQueryData: false, queues: false, stackFrameVariables: false },
      sampleRate: 1.0,
      tracesSampler: function () { return tracesRate(sc, location.pathname); },
      integrations: function (defaults) {
        return defaults.filter(function (i) { return i && i.name !== 'BrowserSession'; })
          .concat([S.browserTracingIntegration({ instrumentNavigation: false, enableInp: true })]);
      },
      tracePropagationTargets: [/^https:\/\/interview\.clozo\.ai\/api\//, /^\/api\//],
      allowUrls: [/^https:\/\/interview\.clozo\.ai\//, new RegExp('^' + location.origin.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '/')],
      denyUrls: [/clarity\.ms/i, /posthog\.com/i, /googletagmanager\.com/i, /google-analytics\.com/i, /bat\.bing\.com/i,
        /connect\.facebook\.net/i, /snap\.licdn\.com/i, /redditstatic\.com/i, /ads-twitter\.com/i,
        /^(chrome|moz|safari|safari-web)-extension:\/\//i],
      ignoreErrors: ['ResizeObserver loop', 'top.GLOBALS', /^Script error\.?$/, 'Non-Error promise rejection captured'],
      maxBreadcrumbs: 30,
      beforeBreadcrumb: scrubBreadcrumb,
      beforeSend: function (ev) { if (sSent >= MAX_PER_PAGE) return null; sSent++; return scrubEvent(ev); },
      beforeSendTransaction: scrubTransaction,
      beforeSendSpan: scrubSpan
    });
    try { S.setTag('page', PAGE); S.setTag('site_build', sc.release || 'unknown'); } catch (_) {}
    sState = 'ready';
    early.push = function () { return early.length; };
    syncUser();
    var buf = early.splice(0, early.length);
    for (var i = 0; i < buf.length; i++) {
      try { var e = buf[i] && buf[i].error; if (e instanceof Error) S.captureException(e); else if (buf[i] && buf[i].message) S.captureMessage(redact(buf[i].message), 'error'); } catch (_) {}
    }
    var p = pending; pending = [];
    for (var j = 0; j < p.length; j++) deliver(p[j]);
  }
  function sOff() {
    sState = 'off'; early.length = 0; early.push = function () { return 0; }; pending = [];
  }
  function sLoad() {
    if (sLoading || sState !== 'idle') return;
    sLoading = true;
    R.config(function (cfg) {
      var sc = cfg && cfg.sentry;
      if (!sc || !sc.dsn || typeof sc.dsn !== 'string') { sOff(); return; }                // D12.15: inert
      var s = document.createElement('script');
      s.src = BUNDLE; s.async = true;
      s.onload = function () { try { sentryInit(sc); } catch (_) { sOff(); } };
      s.onerror = function () { sOff(); };
      document.head.appendChild(s);
    });
  }
  (function hookEarly() {
    var orig = early.push;
    early.push = function () { var r = orig.apply(early, arguments); sLoad(); return r; };
    if (early.length) sLoad();
  })();

  function deliver(item) {
    var S = window.Sentry;
    if (!S) return;
    if (item.code === 'network') { var key = item.route + '|net'; if (netSeen[key]) return; netSeen[key] = true; }
    var level = 'error', fp;
    if (item.code === 'network') { level = 'warning'; fp = ['web-api', item.route, 'network']; }
    else if (item.code === 'non_json') fp = ['web-api', item.route, 'non_json', String(item.status)];
    else if (item.status === 503 && item.code === 'not_hydrated') { level = 'warning'; fp = ['web-api', 'not_hydrated']; }
    else if (item.status === 502 && item.code === 'provider_unavailable' && /\/cancel\//.test(item.route)) {
      fp = ['cancel', 'provider_unavailable', item.provider || 'unknown', item.retryQueued ? 'queued' : 'not_queued']; }
    else if (item.status >= 500) fp = ['web-api', item.route, String(item.status), item.code];
    else if (item.status === 0 && item.area === 'other') { level = 'warning'; fp = ['web', item.code]; }
    else { level = 'warning'; fp = ['web-api', item.route, item.code]; }
    try {
      S.withScope(function (scope) {
        scope.setLevel(level);
        scope.setFingerprint(fp);
        scope.setTag('area', item.area); scope.setTag('route', item.route || '-'); scope.setTag('method', item.method);
        scope.setTag('status', String(item.status)); scope.setTag('code', item.code);
        if (item.provider) scope.setTag('provider', item.provider);
        S.captureMessage('web_failure ' + item.area + ' ' + (item.route || '-') + ' ' + item.code);
      });
    } catch (_) {}
  }
  /** reportFailure(area, info) — OBSERVABILITY §2.7. info: {path, method, status, code, network, nonJson, provider,
      retryQueued} — machine values only. Expected 4xx codes the page already explains are skipped. */
  R.reportFailure = function (area, info) {
    try {
      if (sState === 'off') return;
      info = info || {};
      var code = CODE_RE.test(info.code || '') ? info.code : (info.network ? 'network' : (info.nonJson ? 'non_json' : 'unspecified'));
      var status = typeof info.status === 'number' ? info.status : 0;
      if (!info.network && !info.nonJson && status && status < 500 && EXPECTED.indexOf(code) !== -1) return;
      var item = { area: String(area || 'other').slice(0, 32), route: routeTemplate(info.path), method: String(info.method || 'GET').toUpperCase().slice(0, 8),
        status: status, code: code, provider: /^[a-z][a-z0-9_]{1,31}$/.test(info.provider || '') ? info.provider : null, retryQueued: !!info.retryQueued };
      if (sState === 'ready') deliver(item); else { if (pending.length < 10) pending.push(item); sLoad(); }
    } catch (_) {}
  };
  function currentAccountId() {
    try {
      var u = (window.InterviewAuth && window.InterviewAuth.currentUser) ? window.InterviewAuth.currentUser() : ((storedSession() || {}).user || null);
      return u && isUuid(u.id) ? u.id : null;
    }
    catch (_) { return null; }
  }
  function syncUser() {
    try { if (window.Sentry && sState === 'ready') window.Sentry.setUser(currentAccountId() ? { id: currentAccountId() } : null); } catch (_) {}
  }
  /** setUser(id|null) — uuid only (§2.6); anything else is a no-op. */
  R.setUser = function (id) {
    try { if (!window.Sentry || sState !== 'ready') return;
      if (id === null) window.Sentry.setUser(null); else if (isUuid(id)) window.Sentry.setUser({ id: id }); } catch (_) {}
  };
  window.addEventListener('storage', function (e) { if (e && e.key === 'interview_session') syncUser(); });

  /* ---------------- 2. Clarity (OBSERVABILITY §5.2; D12.10, D12.19, D15.3, D15.21) ---------------- */
  var LABELS = ['signup', 'download_click', 'activation', 'checkout_start', 'purchase',
    'cancel_feedback', 'cancel_reauth', 'cancel_offer', 'cancel_confirm', 'cancel_done',
    'book_view', 'book_cta', 'book_email_click'];
  var cInjected = false, cDead = false, cQueue = [];
  function clarityInject(id, cfg) {
    if (cInjected || !ID_RE.test(id || '')) return false;
    if (stripCapability()) R.reportFailure('other', { code: 'capability_in_url' });            // then load as usual (A7)
    cInjected = true;
    if (!document.querySelector('script[src*="clarity.ms/tag/' + id + '"]')) {
      (function (c, l, a, r, i) {                                                            // Microsoft's standard tag
        c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
        var t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
        var y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
      })(window, document, 'clarity', 'script', id);
    }
    var cl = window.clarity;
    if (typeof cl !== 'function') return true;
    // D15.3: consent "granted" for EVERY visitor, right after the tag, whatever the banner / GPC say.
    try { cl('consentv2', { ad_Storage: 'granted', analytics_Storage: 'granted' }); } catch (_) {}
    try { cl('set', 'page', PAGE); } catch (_) {}
    if (cfg && cfg.sentry && cfg.sentry.release) { try { cl('set', 'site_build', String(cfg.sentry.release).slice(0, 32)); } catch (_) {} }
    // D15.21: NO identify, no custom id, session id or tag that carries an account, an email or a user id.
    var q = cQueue; cQueue = [];
    for (var i = 0; i < q.length; i++) { try { cl('event', q[i]); } catch (_) {} }
    return true;
  }
  // Billing v2 WP6 (A6-12): no Clarity on a page that carries the strict per-page CSP (its policy would refuse the tag).
  var STRICT = (function () {
    try { return !!document.querySelector('meta[http-equiv="Content-Security-Policy"][data-clozo-csp="strict"]'); }
    catch (_) { return false; }
  })();
  function clarityStart() {
    if (STRICT) { cDead = true; cQueue = []; return; }
    if (ID_RE.test(BAKED)) {                                                                 // baked id: no R31 wait
      clarityInject(BAKED, cfgState);
      R.config(function (cfg) {
        var rid = cfg && cfg.clarity && cfg.clarity.projectId;
        if (typeof window.clarity === 'function' && cfg && cfg.sentry && cfg.sentry.release) {
          try { window.clarity('set', 'site_build', String(cfg.sentry.release).slice(0, 32)); } catch (_) {} }
        if (rid && rid !== BAKED) R.reportFailure('other', { code: 'clarity_id_mismatch' });
      });
      return;
    }
    R.config(function (cfg) {                                                                // nothing baked: R31 fallback
      var rid = cfg && cfg.clarity && cfg.clarity.projectId;
      if (!clarityInject(rid, cfg)) { cDead = true; cQueue = []; }                          // no id anywhere = inert
    });
  }
  /** clarityEvent(label) — the fixed content-free label list only; every visitor (not a first-party event). */
  R.clarityEvent = function (name) {
    try {
      if (LABELS.indexOf(name) === -1 || cDead) return;
      if (cInjected && typeof window.clarity === 'function') window.clarity('event', name);
      else if (cQueue.length < 20) cQueue.push(name);
    } catch (_) {}
  };

  /* ---------------- 4. First-party events (R11; D13.3 always on for signed-in visitors) ---------------- */
  var Q = [], stopped = false, pvPending = true, lastFriction = {};
  function storedSession() {                                     // pages without auth.js (blog, use-cases, …): same key
    try {
      var s = JSON.parse(localStorage.getItem('interview_session') || 'null');
      if (!s || !s.token) return null;
      if (typeof s.expiresAtMillis === 'number' && s.expiresAtMillis > 0 && now() >= s.expiresAtMillis) return null;
      return s;
    } catch (_) { return null; }
  }
  function authToken() {
    try {
      if (window.InterviewAuth && typeof window.InterviewAuth.token === 'function') return window.InterviewAuth.token();
      var s = storedSession(); return s ? s.token : null;
    } catch (_) { return null; }
  }
  function uuid() {
    try { if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID(); } catch (_) {}
    var b = new Uint8Array(16);
    try { window.crypto.getRandomValues(b); } catch (_) { for (var i = 0; i < 16; i++) b[i] = Math.floor(Math.random() * 256); }
    b[6] = (b[6] & 15) | 64; b[8] = (b[8] & 63) | 128;
    var h = ''; for (var j = 0; j < 16; j++) h += (b[j] + 256).toString(16).slice(1);
    return h.slice(0, 8) + '-' + h.slice(8, 12) + '-' + h.slice(12, 16) + '-' + h.slice(16, 20) + '-' + h.slice(20);
  }
  var CONTENT_KEY = /text|transcript|reason|message|body|email|token|password|prompt|answer|resume|cookie|authorization|note|name$/i;
  function cleanProps(props) {                                     // machine values only; the server re-validates (§6)
    var out = {};
    if (!props || typeof props !== 'object') return out;
    for (var k in props) {
      if (!props.hasOwnProperty(k) || !/^[A-Za-z][A-Za-z0-9_]{0,40}$/.test(k) || CONTENT_KEY.test(k)) continue;
      var v = props[k];
      if (v === undefined || v === null) continue;
      if (typeof v === 'boolean' || (typeof v === 'number' && isFinite(v))) out[k] = v;
      else if (typeof v === 'string' && /^[0-9A-Za-z_.:-]{1,64}$/.test(v)) out[k] = v;       // ids / enums: no spaces = no prose
    }
    return out;
  }
  function refOf() {
    try {
      var sp = new URLSearchParams(location.search), cap = window.__clozoCap || {};
      if (sp.has('checkout') || (PAGE === 'success' && cap.search)) return 'checkout_return';   // cap.search: success.html's head scrub kept the return query
      if (sp.get('src') === 'app' || cap.src === 'app') return 'app';
      if (sp.get('utm_medium') === 'email' || sp.has('bk')) return 'email';
      var r = document.referrer;
      if (!r) return 'direct';
      return new URL(r).origin === location.origin ? 'nav' : 'external';
    } catch (_) { return 'direct'; }
  }
  var REF = refOf();                                               // read before any page script rewrites the URL
  function enqueue(name, props) {
    if (stopped || !authToken()) return false;                     // signed out → nothing (R11 is A-USER)
    if (pvPending) { pvPending = false; Q.push({ eventID: uuid(), name: 'page_viewed', clientTs: now(), props: { page: PAGE, ref: REF } }); }
    if (name) Q.push({ eventID: uuid(), name: String(name).slice(0, 64), clientTs: now(), props: cleanProps(props) });
    if (Q.length > 200) Q.splice(0, Q.length - 200);
    if (Q.length >= 10) flush(false);
    return true;
  }
  function flush(unloading) {
    if (stopped || !Q.length) return;
    var t = authToken();
    if (!t) { Q.length = 0; return; }
    var batch = Q.splice(0, 50);
    var cfg = cfgState || {};
    var ctx = { surface: 'web', locale: String(navigator.language || '').slice(0, 16) || undefined };
    if (cfg.sentry && cfg.sentry.release) ctx.siteBuild = String(cfg.sentry.release).slice(0, 32);
    try {
      fetch('/api/events', { method: 'POST', keepalive: !!unloading, credentials: 'omit',
        headers: { 'Authorization': 'Bearer ' + t, 'Content-Type': 'application/json' },
        body: JSON.stringify({ context: ctx, events: batch }) })
        .then(function (res) { if (res.status === 401) { stopped = true; Q.length = 0; } }, function () {});
    } catch (_) {}
    if (Q.length && !unloading) flush(false);
    else if (Q.length && unloading) flush(true);
  }
  /** track(name, props) — first-party event; content-free props only (CONTRACT §6.5). */
  R.track = function (name, props) {
    try {
      if (typeof name !== 'string' || !/^[a-z][a-z0-9_]{1,63}$/.test(name)) return;
      if (name === 'download_started') return;                    // D20: server-emitted by R40 only
      enqueue(name, props);
    } catch (_) {}
  };

  /* #55 ui_friction — rage_click (≥ 3 clicks on one data-obs target within 1 s) and repeat_submit (same form twice
     within 2 s); target = the element's data-obs id only, never text; ≤ 1 per target per minute. */
  var clicks = {}, submits = {};
  function obsOf(el) {
    for (var i = 0; el && i < 6; i++, el = el.parentElement) {
      var v = el.getAttribute && el.getAttribute('data-obs');
      if (v && /^[a-z0-9_.:-]{1,64}$/.test(v)) return v;
    }
    return null;
  }
  function friction(kind, target) {
    var key = kind + '|' + target, t = now();
    if (lastFriction[key] && t - lastFriction[key] < 60000) return;
    lastFriction[key] = t;
    R.track('ui_friction', { kind: kind, target: target, page: PAGE });
  }
  document.addEventListener('click', function (e) {
    try {
      var id = obsOf(e.target); if (!id) return;
      var t = now(), a = (clicks[id] || []).filter(function (x) { return t - x < 1000; });
      a.push(t); clicks[id] = a;
      if (a.length >= 3) { clicks[id] = []; friction('rage_click', id); }
    } catch (_) {}
  }, true);
  document.addEventListener('submit', function (e) {
    try {
      var f = e.target, id = obsOf(f) || (f && f.id && /^[a-z0-9_.:-]{1,64}$/i.test(f.id) ? String(f.id).toLowerCase() : null);
      if (!id) return;
      var t = now();
      if (submits[id] && t - submits[id] < 2000) friction('repeat_submit', id);
      submits[id] = t;
    } catch (_) {}
  }, true);

  /* ---------------- boot ---------------- */
  function onHide() { try { if (pvPending) enqueue(null); flush(true); } catch (_) {} }
  window.addEventListener('pagehide', onHide);
  document.addEventListener('visibilitychange', function () { if (document.visibilityState === 'hidden') onHide(); });
  setInterval(function () { try { if (pvPending) enqueue(null); flush(false); } catch (_) {} }, 15000);
  try { enqueue(null); } catch (_) {}                                   // page_viewed now when signed in
  // Clarity waits for the visitor (owner 2026-11-02, SEO C9 — a page's first load at or under 20 requests): it starts on the
  // first pointerdown, keydown, scroll or touchstart, or 5 s after load + idle, whichever comes first — never before load +
  // idle (D8) — and from then on exactly as before: consentv2 granted for every visitor, no identify, never on a strict-CSP
  // page (D12.10, D15.3, D15.21, A6-12). An interaction before load counts: Clarity then starts at load + idle.
  var C_WAIT = 5000, C_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart'];
  var cTouched = false, cReady = false, cTimer = null;
  function cOff() { for (var i = 0; i < C_EVENTS.length; i++) { try { window.removeEventListener(C_EVENTS[i], cTouch, true); } catch (_) {} } }
  function cGo() { if (!cReady) return; cReady = false; cOff(); if (cTimer) { try { clearTimeout(cTimer); } catch (_) {} } clarityStart(); }
  function cTouch() { cTouched = true; cGo(); }
  for (var ci = 0; ci < C_EVENTS.length; ci++) { try { window.addEventListener(C_EVENTS[ci], cTouch, { capture: true, passive: true }); } catch (_) {} }
  function clarityLater() { cReady = true; if (cTouched) cGo(); else cTimer = setTimeout(cGo, C_WAIT); }
  afterLoad(function () { R.config(); sLoad(); clarityLater(); });      // D8: nothing before load + idle

  // Unit-test hooks (pure functions only; no state). check-bake.test.js loads this file in a vm context.
  R._t = { pageOf: pageOf, decode: decode, redact: redact, stripUrl: stripUrl, scrubText: scrubText,
    reduceSelector: reduceSelector, routeTemplate: routeTemplate, scrubEvent: scrubEvent, scrubBreadcrumb: scrubBreadcrumb,
    scrubSpan: scrubSpan, scrubTransaction: scrubTransaction, tracesRate: tracesRate, cleanProps: cleanProps,
    EXPECTED: EXPECTED, LABELS: LABELS };
})();
